/**
 * PortalInviteSetupGuide — step-by-step onboarding guide for an individual invite.
 * Styled to match PortalProjectSetupGuide exactly.
 *
 * Steps:
 *   1. Complete Your Profile
 *   2. Complete Site Induction
 *   3. Receive Digital Badge
 */

import React, { useState, useEffect } from 'react';
import { useNavigate, useOutletContext, useParams } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { Button } from '@shared/components/ui/button';
import {
  User, FileText, BadgeCheck,
  CheckCircle2, ChevronDown, ChevronUp,
  ArrowRight, Clock, Loader2, ShieldCheck,
} from 'lucide-react';
import { supabase } from '@shared/supabaseClient';
import { InviteTabBar } from '@/components/portal/PortalUI';

const PortalInviteSetupGuide = () => {
  const navigate = useNavigate();
  const { submissionId } = useParams();
  const { session, contextInduction } = useOutletContext();

  const email = session?.person?.email;

  const [invite, setInvite] = useState(null);
  const [loading, setLoading] = useState(true);
  const [expandedStep, setExpandedStep] = useState(null);
  const [completionStatus, setCompletionStatus] = useState({
    profile:   false,
    induction: false,
    badge:     false,
  });
  const [inductionStatus, setInductionStatus] = useState(null);

  useEffect(() => {
    if (email && submissionId) loadAll();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [email, submissionId]);

  const loadAll = async () => {
    setLoading(true);
    try {
      // ── Load invite submission ────────────────────────────────────────────
      const { data: sub, error: subErr } = await supabase
        .from('induction_submissions')
        .select('id, status, person_name, person_email, depot_id, public_reference, nature_of_work, work_area, depots:depot_id(id, name)')
        .eq('id', submissionId)
        .maybeSingle();

      if (subErr || !sub) throw new Error('Invite not found');
      setInvite(sub);
      setInductionStatus(sub.status);

      // ── Profile ──────────────────────────────────────────────────────────
      const { data: profile } = await supabase
        .from('portal_profiles')
        .select('person_name, person_phone, person_company, photo_url, vehicle_reg, attending_site_id, profile_completed_at')
        .eq('person_email', email)
        .maybeSingle();

      const profileDone = !!(
        profile?.person_name &&
        profile?.person_phone &&
        profile?.person_company &&
        profile?.photo_url &&
        profile?.vehicle_reg &&
        profile?.attending_site_id &&
        profile?.profile_completed_at
      );

      // ── Induction ────────────────────────────────────────────────────────
      const inductionDone = sub.status === 'submitted' || sub.status === 'approved';
      const badgeDone     = sub.status === 'approved';

      const status = { profile: profileDone, induction: inductionDone, badge: badgeDone };
      setCompletionStatus(status);

      // Auto-expand first incomplete step
      const steps = getSteps(sub);
      const firstIncomplete = steps.find(s => !status[s.id]);
      if (firstIncomplete) setExpandedStep(firstIncomplete.id);
    } catch (err) {
      console.error('Error loading invite setup guide:', err);
    } finally {
      setLoading(false);
    }
  };

  const getInductionAction = (status) => {
    if (status === 'in_progress') return 'Continue Induction';
    if (status === 'pending_acknowledgement') return 'Review & Confirm';
    if (status === 'submitted' || status === 'approved') return 'Review';
    return 'Start Induction';
  };

  const getSteps = (sub) => [
    {
      id: 'profile',
      icon: User,
      title: 'Complete Your Profile',
      description: 'Your name, company, photo, phone, and vehicle registration must be on file before you can access site.',
      action: 'Set Up Profile',
      route: '/portal/profile',
      why: 'Required for site access',
      time: '2 minutes',
    },
    {
      id: 'induction',
      icon: FileText,
      title: 'Complete Site Induction',
      description: `Read and complete the induction for ${sub?.depots?.name || 'this site'}. Covers safety, PPE, emergency procedures and site rules.`,
      action: getInductionAction(sub?.status),
      route: `/portal/inductions/${submissionId}/brief`,
      why: 'Legally required before entering site',
      time: '5–10 minutes',
    },
    {
      id: 'badge',
      icon: BadgeCheck,
      title: 'Receive Digital Site Pass',
      description: 'Once your induction is approved by the site administrator, your digital QR pass is issued automatically. Present it at the gate kiosk on arrival.',
      action: 'View Badge',
      route: `/portal/digital-badge?submission=${submissionId}`,
      why: 'Your site access credential',
      time: 'Once induction approved',
    },
  ];

  if (loading) {
    return (
      <div>
        <InviteTabBar submissionId={submissionId} inviteStatus={contextInduction?.status} />
        <div className="min-h-[60vh] flex items-center justify-center">
          <Loader2 className="h-8 w-8 animate-spin text-primary" />
        </div>
      </div>
    );
  }

  const steps = getSteps(invite);
  const completedCount = steps.filter(s => completionStatus[s.id]).length;
  const allDone = steps.every(s => completionStatus[s.id]);

  return (
    <div>
      <InviteTabBar submissionId={submissionId} inviteStatus={invite?.status || contextInduction?.status} />
      <div className="max-w-2xl mx-auto py-8 space-y-6">

      {/* Header */}
      <motion.div initial={{ opacity: 0, y: -12 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.3 }}>
        <div className="flex items-center gap-2 mb-4">
          <div className="p-1.5 rounded-lg bg-primary/10">
            <ShieldCheck className="h-4 w-4 text-primary" />
          </div>
          <span className="text-xs font-bold text-primary uppercase tracking-widest">
            {invite?.depots?.name || 'Site Invite'}
          </span>
        </div>
        <h1 className="text-2xl font-bold text-foreground mb-1">Setup Guide</h1>
        <p className="text-sm font-medium text-muted-foreground">
          {invite?.depots?.name}
          {invite?.public_reference && <span className="ml-2">· {invite.public_reference}</span>}
        </p>
        {invite?.nature_of_work && (
          <p className="text-xs text-muted-foreground mt-1">
            <span className="font-semibold uppercase tracking-wide">Nature of visit:</span> {invite.nature_of_work}
            {invite.work_area && <span className="ml-2">· {invite.work_area}</span>}
          </p>
        )}

        {/* Progress bar */}
        <div className="mt-4 p-4 rounded-xl border border-border bg-card">
          <div className="flex items-center justify-between text-sm mb-2">
            <span className="font-semibold text-foreground">Progress</span>
            <span className="font-bold text-primary">{completedCount} / {steps.length} complete</span>
          </div>
          <div className="h-2.5 bg-muted rounded-full overflow-hidden">
            <motion.div
              initial={{ width: 0 }}
              animate={{ width: `${(completedCount / steps.length) * 100}%` }}
              transition={{ duration: 0.5, ease: 'easeOut' }}
              className="h-full rounded-full bg-primary"
            />
          </div>
        </div>
      </motion.div>

      {/* All-done banner */}
      <AnimatePresence>
        {allDone && (
          <motion.div
            initial={{ opacity: 0, scale: 0.97 }}
            animate={{ opacity: 1, scale: 1 }}
            exit={{ opacity: 0 }}
            className="p-5 rounded-xl bg-green-50 border border-green-300 flex items-center gap-4"
          >
            <div className="p-2.5 rounded-full bg-green-100 flex-shrink-0">
              <CheckCircle2 className="h-6 w-6 text-green-700" />
            </div>
            <div className="flex-1">
              <p className="font-bold text-green-900">All set — you're ready to visit site!</p>
              <p className="text-sm font-medium text-green-800">Your induction is approved and your digital site pass has been issued.</p>
            </div>
            <Button
              onClick={() => navigate(`/portal/digital-badge?submission=${submissionId}`)}
              className="bg-green-700 hover:bg-green-800 text-white flex-shrink-0"
            >
              View Pass <ArrowRight className="ml-2 h-4 w-4" />
            </Button>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Steps */}
      <div className="space-y-2">
        {steps.map((step, index) => {
          const done       = completionStatus[step.id];
          const isExpanded = expandedStep === step.id;
          const Icon       = step.icon;
          const locked     = index > 0 && !completionStatus[steps[index - 1].id];
          const displayNum = index + 1;

          return (
            <motion.div
              key={step.id}
              initial={{ opacity: 0, y: 8 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.2, delay: index * 0.04 }}
            >
              <div className={`rounded-xl border overflow-hidden transition-all duration-200
                ${ done    ? 'border-green-300 bg-green-50'
                : locked   ? 'border-border bg-muted/30 opacity-60'
                           : 'border-primary/40 bg-card shadow-sm' }`}
              >
                <button
                  type="button"
                  className="w-full text-left px-4 py-3.5 flex items-center gap-3 disabled:cursor-not-allowed"
                  onClick={() => !locked && setExpandedStep(isExpanded ? null : step.id)}
                  disabled={locked}
                >
                  <div className={`flex-shrink-0 w-9 h-9 rounded-full flex items-center justify-center
                    ${ done ? 'bg-green-100' : locked ? 'bg-muted' : 'bg-primary/10' }`}
                  >
                    {done
                      ? <CheckCircle2 className="h-5 w-5 text-green-700" />
                      : <Icon className={`h-4 w-4 ${locked ? 'text-muted-foreground' : 'text-primary'}`} />
                    }
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 flex-wrap mb-0.5">
                      <span className={`text-xs font-bold uppercase tracking-wider
                        ${ done ? 'text-green-700' : 'text-muted-foreground' }`}>
                        Step {displayNum}
                      </span>
                      {done && (
                        <span className="inline-flex items-center rounded-full bg-green-100 border border-green-300 px-2 py-0 text-xs font-bold text-green-800">
                          Complete
                        </span>
                      )}
                      {!done && !locked && (
                        <span className="inline-flex items-center rounded-full bg-primary/10 border border-primary/30 px-2 py-0 text-xs font-bold text-primary">
                          Action needed
                        </span>
                      )}
                      {!done && locked && (
                        <span className="inline-flex items-center rounded-full bg-muted border border-border px-2 py-0 text-xs font-semibold text-muted-foreground">
                          Complete step {displayNum - 1} first
                        </span>
                      )}
                    </div>
                    <p className={`font-bold text-sm ${ done ? 'text-muted-foreground line-through' : 'text-foreground' }`}>
                      {step.title}
                    </p>
                  </div>
                  {!locked && (
                    isExpanded
                      ? <ChevronUp className="h-4 w-4 text-muted-foreground flex-shrink-0" />
                      : <ChevronDown className="h-4 w-4 text-muted-foreground flex-shrink-0" />
                  )}
                </button>

                <div style={{ display: 'grid', gridTemplateRows: isExpanded && !locked ? '1fr' : '0fr', transition: 'grid-template-rows 0.18s ease' }}>
                  <div className="overflow-hidden">
                    <div className="px-4 pb-4 pt-0 border-t border-border">
                      <p className="text-sm font-medium text-muted-foreground leading-relaxed mt-3 mb-4">
                        {step.description}
                      </p>
                      <div className="flex items-center gap-5 mb-4 text-xs font-semibold text-muted-foreground">
                        <span className="flex items-center gap-1.5">
                          <ShieldCheck className="h-3.5 w-3.5 text-primary" />
                          {step.why}
                        </span>
                        <span className="flex items-center gap-1.5">
                          <Clock className="h-3.5 w-3.5 text-muted-foreground" />
                          {step.time}
                        </span>
                      </div>
                      <Button
                        size="sm"
                        onClick={() => navigate(step.route, { state: { returnTo: `/portal/inductions/${submissionId}/setup` } })}
                        variant={done ? 'outline' : 'default'}
                        className={done ? 'border-border text-foreground' : ''}
                      >
                        {done ? 'Review' : step.action}
                        <ArrowRight className="ml-2 h-3.5 w-3.5" />
                      </Button>
                    </div>
                  </div>
                </div>
              </div>
            </motion.div>
          );
        })}
      </div>

      {/* Footer */}
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ delay: 0.35 }}
        className="flex justify-end pt-2"
      >
        <Button
          variant="ghost"
          size="sm"
          onClick={() => navigate(`/portal/inductions/${submissionId}`)}
          className="text-muted-foreground hover:text-foreground"
        >
          Back to Invite <ArrowRight className="ml-1 h-3.5 w-3.5" />
        </Button>
      </motion.div>
      </div>
    </div>
  );
};

export default PortalInviteSetupGuide;
